import React from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  VStack,
  FormControl,
  FormErrorMessage,
  Button,
  HStack,
  Input,
  Radio,
  RadioGroup,
  Box,
  Text,
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { ThemedFormLabel } from "../../../homeowners/hire/themed-form-label";
import {
  SalesConversationPreferenceFormData,
  salesConversationPreferenceSchema,
  PreferredSalesConversationTime,
} from "../../schemas/schema";

interface SalesConversationPreferenceStepProps {
  onStepComplete: (data: SalesConversationPreferenceFormData) => void;
  onPrevious: () => void;
  isLoading?: boolean;
  defaultValues?: Partial<SalesConversationPreferenceFormData>;
}

const SALES_CONVERSATION_TIME_OPTIONS = [
  {
    value: PreferredSalesConversationTime.MORNING,
    label: "Morning (9:00 AM - 11:00 AM)",
  },
  {
    value: PreferredSalesConversationTime.AFTERNOON,
    label: "Afternoon (12:00 PM - 5:00 PM)",
  },
  {
    value: PreferredSalesConversationTime.EVENING,
    label: "Evening (6:00 PM - 8:00 PM)",
  },
];

export const SalesConversationPreferenceStep: React.FC<SalesConversationPreferenceStepProps> = ({
  onStepComplete,
  onPrevious,
  isLoading = false,
  defaultValues,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<SalesConversationPreferenceFormData>({
    resolver: zodResolver(salesConversationPreferenceSchema),
    defaultValues: {
      ...defaultValues,
      preferredSalesConversationDate: defaultValues?.preferredSalesConversationDate || undefined,
      preferredSalesConversationTime: defaultValues?.preferredSalesConversationTime || undefined,
    },
  });

  const onSubmit = (data: SalesConversationPreferenceFormData) => onStepComplete(data);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={6} align="stretch">
        <FormControl isInvalid={!!errors.preferredSalesConversationDate}>
          <ThemedFormLabel>Preferred date</ThemedFormLabel>
          <Controller
            name="preferredSalesConversationDate"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                type="date"
                size="lg"
                variant="outline"
                rounded="none"
                borderColor="gray.400"
                value={field.value ? field.value.toISOString().split("T")[0] : ""}
                onChange={(e) => field.onChange(new Date(e.target.value))}
              />
            )}
          />
          <FormErrorMessage>{errors.preferredSalesConversationDate?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors.preferredSalesConversationTime}>
          <ThemedFormLabel>What time works best for you?</ThemedFormLabel>
          <Controller
            name="preferredSalesConversationTime"
            control={control}
            render={({ field }) => (
              <RadioGroup value={field.value} onChange={field.onChange}>
                <VStack align="stretch" spacing={3}>
                  {SALES_CONVERSATION_TIME_OPTIONS.map((option) => (
                    <Box
                      key={option.value}
                      p={3}
                      border="1px"
                      borderColor="gray.200"
                      rounded="none"
                      cursor="pointer"
                      _hover={{
                        borderColor: "flatteredFlamingo.500",
                        bg: "gray.50",
                      }}
                    >
                      <Radio value={option.value}>
                        <VStack align="start" spacing={0.5} pl={2}>
                          <Text fontSize="md" fontWeight="medium">
                            {option.label}
                          </Text>
                        </VStack>
                      </Radio>
                    </Box>
                  ))}
                </VStack>
              </RadioGroup>
            )}
          />
          <FormErrorMessage>{errors.preferredSalesConversationTime?.message}</FormErrorMessage>
        </FormControl>

        <HStack spacing={4} mt={4}>
          <Button
            type="button"
            onClick={onPrevious}
            leftIcon={<ArrowBackIcon />}
            size="lg"
            flex="1"
            variant="outline"
            isDisabled={isLoading}
          >
            Previous
          </Button>
          <Button
            type="submit"
            size="lg"
            flex="1"
            colorScheme="flatteredFlamingo"
            isLoading={isLoading}
            loadingText="Submitting..."
          >
            Submit Request
          </Button>
        </HStack>
      </VStack>
    </form>
  );
};
